export async function compressImage(blob: Blob, maxDimension = 800, quality = 0.65): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = Math.round(height * (maxDimension / width));
          width = maxDimension;
        } else {
          width = Math.round(width * (maxDimension / height));
          height = maxDimension;
        }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Could not get canvas context"));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (compressed) => {
          if (compressed) resolve(compressed);
          else reject(new Error("Canvas toBlob returned null"));
        },
        "image/jpeg",
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image for compression"));
    };
    img.src = url;
  });
}

/**
 * Reliable client-side compression for *all* devices/browsers.
 * Goal: consistently produce small selfies (~<=100KB) before upload.
 * This keeps the base64 sent to QuickBase small.
 *
 * Strategy:
 * - Canvas + JPEG (the only widely reliable cross-device approach without extra libs).
 * - Start reasonably aggressive.
 * - Iteratively reduce dimension + quality.
 * - Always returns a usable Blob: falls back to the smallest successful result,
 *   or the input rawBlob if nothing better could be produced. Never throws
 *   in a way that prevents punch submission.
 */
export async function compressSelfieForUpload(rawBlob: Blob): Promise<Blob> {
  const TARGET = 100 * 1024; // ~100KB target for good cellular + small QB attachments
  let dim = 800;
  let q = 0.65;
  let best: Blob = rawBlob;

  // Up to 6 attempts for consistency across devices with varying camera resolutions
  for (let i = 0; i < 6; i++) {
    try {
      const out = await compressImage(rawBlob, dim, q);
      best = out;
      if (out.size <= TARGET) {
        return out;
      }
    } catch {
      // One step failed (rare). Keep best so far and continue lowering.
    }
    // Reduce more aggressively on later attempts
    dim = Math.max(480, Math.round(dim * 0.78));
    q = Math.max(0.35, q - 0.1);
  }
  return best;
}

export function createObjectUrlSafe(blob: Blob): string {
  try {
    return URL.createObjectURL(blob);
  } catch {
    return "";
  }
}
